import { Link, Outlet, useRouterState } from '@tanstack/react-router';
import { Check, Copy } from 'lucide-react';
import { useState } from 'react';
import { Alert, Button, Dialog, DialogContent, Input, PageHeader, Select, cn, useToast, segmentedItemClass, segmentedListClass } from '@buildbee/ui';
import { trpc } from '../../lib/trpc';

/** Shared layout, role hints and small pickers for the integrations screens. */

type Role = 'owner' | 'admin' | 'sales' | 'pm' | 'field' | 'sub' | 'client';

export function useRole(): { role: Role | null; isAdmin: boolean; canEmail: boolean; loading: boolean } {
  const current = trpc.workspace.current.useQuery();
  const role = (current.data?.permissions.role ?? null) as Role | null;
  return {
    role,
    isAdmin: role === 'owner' || role === 'admin',
    canEmail: role === 'owner' || role === 'admin' || role === 'sales' || role === 'pm',
    loading: current.isLoading,
  };
}

const TABS = [
  { to: '/integrations', label: 'Overview', exact: true, show: () => true },
  { to: '/integrations/email', label: 'Email accounts', show: (r: ReturnType<typeof useRole>) => r.canEmail },
  { to: '/integrations/inbox', label: 'Synced email', show: (r: ReturnType<typeof useRole>) => r.canEmail },
  { to: '/integrations/calendar', label: 'Calendar', show: (r: ReturnType<typeof useRole>) => r.canEmail },
  { to: '/integrations/storage', label: 'Cloud storage', show: (r: ReturnType<typeof useRole>) => r.isAdmin },
  { to: '/integrations/ai', label: 'AI assistant', show: () => true },
  { to: '/integrations/webhooks', label: 'Webhooks', show: (r: ReturnType<typeof useRole>) => r.isAdmin },
  { to: '/integrations/api-keys', label: 'API keys', show: (r: ReturnType<typeof useRole>) => r.isAdmin },
] as const;

export function IntegrationsLayout() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const role = useRole();
  return (
    <div>
      <PageHeader title="Integrations" description="Email and calendar sync, AI drafting, webhooks and the public API." />
      <nav aria-label="Integrations" className={cn(segmentedListClass, 'mb-6')}>
        {TABS.filter((t) => role.loading || t.show(role)).map((t) => {
          const active = 'exact' in t ? pathname === t.to || pathname === `${t.to}/` : pathname.startsWith(t.to);
          return (
            <Link
              key={t.to}
              to={t.to}
              className={segmentedItemClass(active)}
            >
              {t.label}
            </Link>
          );
        })}
      </nav>
      <Outlet />
    </div>
  );
}

export function CopyField({ value, label }: { value: string; label: string }) {
  const toast = useToast();
  const [copied, setCopied] = useState(false);
  return (
    <div className="flex gap-2">
      <Input readOnly value={value} aria-label={label} className="font-mono text-xs" onFocus={(e) => e.currentTarget.select()} />
      <Button
        type="button"
        variant="outline"
        size="icon"
        aria-label={`Copy ${label}`}
        onClick={async () => {
          try {
            await navigator.clipboard.writeText(value);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          } catch {
            toast.error('Could not copy', 'Select the text and copy it manually.');
          }
        }}
      >
        {copied ? <Check /> : <Copy />}
      </Button>
    </div>
  );
}

/** Shows a secret exactly once after creation/rotation. */
export function SecretDialog({ title, description, secret, onClose }: { title: string; description: string; secret: string | null; onClose: () => void }) {
  return (
    <Dialog open={!!secret} onOpenChange={(open) => !open && onClose()}>
      <DialogContent title={title} description={description} footer={<Button onClick={onClose}>I saved it</Button>}>
        {secret ? (
          <div className="space-y-3">
            <CopyField value={secret} label="secret" />
            <Alert variant="warning">You will not be able to see this again. Store it in your secret manager now.</Alert>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

export function ProjectSelect({ value, onChange, id, required }: { value: string; onChange: (v: string) => void; id?: string; required?: boolean }) {
  const projects = trpc.integrations.lookup.projects.useQuery({});
  return (
    <Select
      id={id}
      value={value}
      required={required}
      onChange={(e) => onChange(e.target.value)}
      placeholder={projects.isLoading ? 'Loading projects…' : projects.data?.length ? 'Choose a project' : 'No projects available'}
      options={(projects.data ?? []).map((p) => ({ value: p.id, label: `${p.number} · ${p.name}` }))}
    />
  );
}

export function DealSelect({ value, onChange, id, allowEmpty }: { value: string; onChange: (v: string) => void; id?: string; allowEmpty?: boolean }) {
  const deals = trpc.integrations.lookup.deals.useQuery({});
  return (
    <Select
      id={id}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={deals.isLoading ? 'Loading deals…' : allowEmpty ? 'None' : 'Choose a deal'}
      options={(deals.data ?? []).map((d) => ({ value: d.id, label: `${d.title}${d.contactName ? ` · ${d.contactName}` : ''}${d.status !== 'open' ? ` (${d.status})` : ''}` }))}
    />
  );
}

export function today(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** `<input type="datetime-local">` value for a Date (local time). */
export function toLocalInput(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
